"use client";

import { useCallback, useRef, useState, type FormEvent } from "react";
import { WebAuthnPrompt } from "@/components/auth/webauthn-prompt";

interface MFAFormProps {
  userId: string;
  mfaMethods: string[];
  onSubmit: (code: string) => Promise<void>;
  onWebAuthnComplete: (credential: Record<string, unknown>) => Promise<void>;
  onBack: () => void;
}

export function MFAForm({
  userId,
  mfaMethods,
  onSubmit,
  onWebAuthnComplete,
  onBack,
}: MFAFormProps) {
  const hasWebAuthn = mfaMethods.includes("webauthn");
  const hasTotp = mfaMethods.includes("totp");

  // When WebAuthn is available, start in WebAuthn mode and fall back to TOTP on cancel.
  const [showTotp, setShowTotp] = useState(!hasWebAuthn);

  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submittingRef = useRef(false);

  const doSubmitTotp = useCallback(
    async (value: string) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      setIsLoading(true);
      setError(null);
      try {
        await onSubmit(value);
      } catch {
        setError("Verification failed. Please try again.");
      } finally {
        setIsLoading(false);
        submittingRef.current = false;
      }
    },
    [onSubmit],
  );

  const handleTotpSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      await doSubmitTotp(code);
    },
    [code, doSubmitTotp],
  );

  const handleTotpChange = useCallback(
    (value: string) => {
      const digits = value.replace(/\D/g, "");
      setCode(digits);
      if (digits.length === 6) {
        void doSubmitTotp(digits);
      }
    },
    [doSubmitTotp],
  );

  const handleWebAuthnSuccess = useCallback(
    async (credential: Record<string, unknown>) => {
      setIsLoading(true);
      setError(null);
      try {
        await onWebAuthnComplete(credential);
      } catch {
        if (hasTotp) {
          setError("Authentication failed. Please try your authenticator app.");
          setShowTotp(true);
        } else {
          setError("Authentication failed. Please try again or sign in with your password.");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [onWebAuthnComplete, hasTotp],
  );

  const handleWebAuthnFallback = useCallback(() => {
    // User cancelled browser biometric dialog — silently switch to TOTP
    setShowTotp(true);
  }, []);

  // ── WebAuthn mode (auto-triggered on mount) ──────────────────────────────
  if (!showTotp) {
    return (
      <div className="rounded-xl border border-border bg-surface p-6 shadow-sm sm:p-8">
        <h2 className="mb-2 text-lg font-medium text-text-primary">
          Two-factor authentication
        </h2>
        <p className="mb-6 text-sm text-text-secondary">
          Confirm your identity with your registered fingerprint or device biometric.
        </p>

        {error && (
          <div className="mb-4 rounded-lg bg-error-light px-3 py-2.5 text-sm text-error">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <WebAuthnPrompt
            mode="authenticate"
            userId={userId}
            onSuccess={(cred) => void handleWebAuthnSuccess(cred)}
            {...(hasTotp ? { onFallback: handleWebAuthnFallback } : {})}
            onError={(msg) => {
              setError(msg);
              if (hasTotp) setShowTotp(true);
            }}
          />

          {hasTotp && (
            <button
              type="button"
              onClick={() => setShowTotp(true)}
              disabled={isLoading}
              className="w-full rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-text-secondary hover:bg-surface-hover hover:text-text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface focus:outline-none disabled:opacity-50 max-md:min-h-11"
            >
              Use authenticator app instead
            </button>
          )}

          <button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            className="w-full px-4 py-2 text-sm text-text-tertiary hover:text-text-secondary focus:outline-none disabled:opacity-50"
          >
            &larr; Back to sign in
          </button>
        </div>
      </div>
    );
  }

  // ── TOTP mode ─────────────────────────────────────────────────────────────
  return (
    <div className="rounded-xl border border-border bg-surface p-6 shadow-sm sm:p-8">
      <h2 className="mb-2 text-lg font-medium text-text-primary">
        Two-factor authentication
      </h2>
      <p className="mb-6 text-sm text-text-secondary">
        Enter the 6-digit code from your authenticator app.
      </p>

      {error && (
        <div className="mb-4 rounded-lg bg-error-light px-3 py-2.5 text-sm text-error">
          {error}
        </div>
      )}

      <form onSubmit={handleTotpSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="mfa-code"
            className="mb-1.5 block text-sm font-medium text-text-primary"
          >
            Verification code
          </label>
          <input
            id="mfa-code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={(e) => handleTotpChange(e.target.value)}
            disabled={isLoading}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-center font-mono text-lg tracking-[0.3em] text-text-primary focus:border-border-focus focus:ring-2 focus:ring-ring focus:outline-none disabled:opacity-50 max-md:min-h-12"
            placeholder="000000"
          />
        </div>

        <button
          type="submit"
          disabled={isLoading || code.length !== 6}
          className="w-full rounded-lg bg-accent px-4 py-2.5 text-sm font-medium text-accent-text shadow-sm hover:bg-accent-hover focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface focus:outline-none disabled:opacity-50 max-md:min-h-11"
        >
          {isLoading ? "Verifying..." : "Verify"}
        </button>

        {/* Offer fingerprint switch if user has WebAuthn but navigated back to TOTP */}
        {hasWebAuthn && (
          <button
            type="button"
            onClick={() => { setShowTotp(false); setCode(""); setError(null); }}
            disabled={isLoading}
            className="w-full rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-text-secondary hover:bg-surface-hover hover:text-text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface focus:outline-none disabled:opacity-50 max-md:min-h-11"
          >
            Use fingerprint instead
          </button>
        )}

        <button
          type="button"
          onClick={onBack}
          disabled={isLoading}
          className="w-full rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-text-secondary hover:bg-surface-hover hover:text-text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface focus:outline-none disabled:opacity-50"
        >
          Back to sign in
        </button>
      </form>
    </div>
  );
}
